import * as React from "react";
import { Check, Upload } from "lucide-react";
import { Badge, Spinner, Tooltip } from "@/components/ui";
import { cn } from "@/lib/utils";
import { BackgroundAsset, BackgroundRatio } from "@/services/bedrock/dtos/BackgroundAsset";
import { aspectClass } from "./ratios";

export interface BackgroundTileCropProps {
  asset: Pick<BackgroundAsset, "id" | "url" | "focal"> &
    Partial<Pick<BackgroundAsset, "source" | "description">> & {
      ratios?: BackgroundRatio[];
      /** From the server for the requested ratio; `false` shows the crops-tight badge. */
      compatible?: boolean;
    };
  ratio: BackgroundRatio;
  selected?: boolean;
  /** Already attached: disabled with a check. */
  disabled?: boolean;
  /** Being added right now: disabled with a spinner. */
  busy?: boolean;
  badge?: React.ReactNode;
  onClick?(): void;
  onMenu?: React.ReactNode;
  /** Leaves off the upload glyph (the coach's Backgrounds tab says whose a photo is elsewhere). */
  hideUploadGlyph?: boolean;
  className?: string;
}

/** A background cropped to `ratio` the way a template shows it: `object-cover` around the tagged focal point. */
export const BackgroundTileCrop: React.FC<BackgroundTileCropProps> = ({
  asset,
  ratio,
  selected,
  disabled,
  busy,
  badge,
  onClick,
  onMenu,
  hideUploadGlyph = false,
  className,
}) => {
  const image = (
    <img
      src={asset.url}
      alt={asset.description || "Background photo"}
      className="size-full object-cover"
      style={{ objectPosition: asset.focal }}
      draggable={false}
    />
  );

  const frameClass = cn(
    "relative block w-full overflow-hidden rounded-lg border border-slate-200 bg-slate-100",
    aspectClass(ratio),
    selected && "ring-2 ring-primary ring-offset-1",
    onClick && !disabled && !busy && "cursor-pointer hover:border-primary"
  );

  return (
    <div
      className={cn("group/tile relative", className)}
      data-testid="background-tile-crop"
      aria-disabled={disabled || undefined}
    >
      {onClick ? (
        <button
          type="button"
          className={frameClass}
          onClick={onClick}
          disabled={disabled || busy}
          aria-pressed={selected}
          aria-busy={busy || undefined}
        >
          {image}
        </button>
      ) : (
        <div className={frameClass}>{image}</div>
      )}

      {busy && (
        <span className="pointer-events-none absolute inset-0 flex items-center justify-center rounded-lg bg-white/60">
          <Spinner className="size-4 text-slate-600" />
        </span>
      )}

      {disabled && (
        <span
          className="pointer-events-none absolute inset-0 flex items-center justify-center rounded-lg bg-white/60"
          data-testid="tile-attached"
        >
          <Check className="size-5 text-primary" aria-hidden="true" />
        </span>
      )}

      {asset.source === "uploaded" && !hideUploadGlyph && (
        <Tooltip>
          <Tooltip.Trigger
            render={
              <span
                className="absolute top-1 left-1 flex size-4 items-center justify-center rounded bg-white/90 text-slate-600"
                data-testid="uploaded-glyph"
              />
            }
          >
            <Upload className="size-3" aria-label="Uploaded" />
          </Tooltip.Trigger>
          <Tooltip.Content>Uploaded by your team</Tooltip.Content>
        </Tooltip>
      )}

      {asset.compatible === false && (
        <Tooltip>
          <Tooltip.Trigger
            render={
              <span className="absolute bottom-1 left-1 flex max-w-[calc(100%-0.5rem)]" data-testid="crops-tight" />
            }
          >
            <Badge variant="secondary" size="small" className="truncate">
              Crops tightly
            </Badge>
          </Tooltip.Trigger>
          <Tooltip.Content>This photo will be cropped a lot at this size.</Tooltip.Content>
        </Tooltip>
      )}

      {badge}
      {onMenu && <div className="absolute top-0.5 right-0.5">{onMenu}</div>}
    </div>
  );
};
